import {
  Block,
  ButtonProps,
  ButtonType,
  ButtonWidget,
  conditional,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  InlineBlock,
  TabStyles,
  testId,
} from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../codegen/Localize';
import { ProfilePictureStyles } from '../styles/ProfilePictureStyles';
import { UserProfilePicture } from './UserProfilePicture';

const DIALOG_WIDTH: number = 500;
const DIALOG_CONTENT_HEIGHT: number = 300;

export function ChooseProfilePictureDialog(props: ChooseProfilePictureDialog.Props): JSX.Element {

  const buttons = [
    {
      handleClick: () => props.handleChooseFromComputerButtonClick(),
      buttonType: ButtonType.Outline,
      children: Messages.uploadFromComputerButton(),
      testId: 'profile-picture-choose'
    },
    {
      handleClick: () => {
        props.handleSubmitRequest();
        props.onEditModeUpdate(false);
      },
      buttonType: ButtonType.Primary,
      disabled: props.previewContainsEdit === false,
      children: Messages.done(),
      testId: 'profile-picture-upload'
    }
  ];

  // Temporarily use inline buttons wrapper before shared-widget accepts config params for different buttons positioning,
  // such as 'TogetherRight' or 'SpacedEvenly'. To be replaced with the when DialogFooterButtonsWidget is updated
  interface DialogFooterButtonsStyle {
    ButtonsContainer: React.CSSProperties;
    FooterButton: React.CSSProperties;
  }
  const footerButtonsStyles: DialogFooterButtonsStyle = {
    ButtonsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '100%'
    },
    FooterButton: {
      paddingLeft: TabStyles.Spacing.MediumSpace
    }
  };

  const dialogFooterComponent: JSX.Element = (
    <Block
      data-tb-test-id={testId('Edit-User-Photo-Dialog-Footer', 'Confirm-Cancel-Footer')}
      style={footerButtonsStyles.ButtonsContainer}>
      {buttons.map((buttonProp: ButtonProps, idx: number) => {
        return (
          <InlineBlock
            style={conditional(idx !== 0, footerButtonsStyles.FooterButton)}
            key={idx}>
            <ButtonWidget {...buttonProp}/>
          </InlineBlock>
        );
      })}
    </Block>
  );

  const imagePreviewComponent: JSX.Element =
    <UserProfilePicture
      imageUrl={props.imagePreviewUrl}
      profileDisplayName={props.profileDisplayName}
      imageSize={ProfilePictureStyles.Sizes.LargeIconSize}
      fontSize={ProfilePictureStyles.FontSizes.LargeIconFontSize}
      divStyle={ProfilePictureStyles.Layout.CenterSingleChild}
    />;

  return (
    <DialogWidget
      shouldReturnFocusOnClose={false}
      dialogWidth={DIALOG_WIDTH}
      testId="edit-user-photo-dialog"
      type={DialogType.NormalWithTitleBar}
      title={Messages.editUserPhotoDialog_title()}
      positioningBehavior={DialogPositioningBehavior.Draggable}
      handleGlassClick={() => props.onEditModeUpdate(false)}
      isModal={true}
      showCloseButton={true}
      onCloseRequest={() => props.onEditModeUpdate(false)}
      showFooter={true}
      footerContent={dialogFooterComponent}>
      <Block style={{height: DIALOG_CONTENT_HEIGHT}}>
        {imagePreviewComponent}
      </Block>
    </DialogWidget>
  );

}

export module ChooseProfilePictureDialog {

  export interface Props {
    handleChooseFromComputerButtonClick: () => void;
    imagePreviewUrl: string;
    profileDisplayName: string;
    previewContainsEdit: boolean;
    onEditModeUpdate: (inEditMode: boolean) => void;
    handleSubmitRequest: () => void;
  }

}
